import { useMcpTool } from 'webmcp-react'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { InboxesApi } from 'entities/Inbox'
import { MessagesApi } from 'entities/Message'
import { listInboxesSchema, sendMessageSchema } from 'entities/WebMcp/model/schemas'
import { serializeInbox } from 'entities/WebMcp/lib/serializers'
import {
  createConversationForContact,
  openConversationInInbox,
  inboxOutcome,
} from 'entities/WebMcp/lib/conversationNavigation'
import { okResult, errorResult } from 'entities/WebMcp/lib/toolResult'

/**
 * Registers the WebMCP messaging tools. Renders nothing.
 *
 * send_message is a write — it has NO readOnlyHint, so the agent gates it
 * behind the approve/deny card. Sending is a two-step server flow done
 * client-side: get-or-create the conversation for (contact, inbox), then
 * create the message.
 */
export const MessagingTools = () => {
  const { inboxesStore } = useEntitiesContext()

  useMcpTool({
    name: 'list_inboxes',
    description:
      'List the sending inboxes/teams. Use an inbox id as team_id when sending. eligible=false inboxes cannot send.',
    input: listInboxesSchema,
    annotations: { readOnlyHint: true },
    handler: async () => {
      try {
        const { data } = await InboxesApi.getTeamsAll()
        return okResult(data.map(serializeInbox))
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  useMcpTool({
    name: 'send_message',
    description:
      'Send an SMS to a contact from a sending inbox. Requires contact_id, message, and team_id (an inbox id from list_inboxes). Call list_inboxes first and pick an eligible inbox; ask the user which one if it is unclear. Sending always asks the user to approve it first. Sending the same text to several contacts in one reply is a single approval that lists every recipient, so batch them rather than sending one turn at a time.',
    input: sendMessageSchema,
    handler: async ({ contact_id, message, team_id }) => {
      try {
        const conversation = await createConversationForContact(
          inboxesStore,
          contact_id,
          Number(team_id)
        )
        await MessagesApi.createMessage(conversation.id, { message, media_url: [] })
        const outcome = inboxOutcome(conversation, Number(team_id))
        await openConversationInInbox(inboxesStore, conversation.id, outcome.inbox_id)
        const { agent_guidance, ...data } = outcome
        return okResult({ sent: true, conversation_id: conversation.id, ...data }, agent_guidance)
      } catch (e) {
        return errorResult(e)
      }
    },
  })

  return null
}
